import React, { FC, useCallback, useMemo, useState } from "react";
import { FormGroup, Label } from "reactstrap";
import __ from "src/utils/Translations";
import Select from "react-select";
import { debounce } from "lodash";
import { AxiosError } from "axios";
import useFetchPotentialAreasToMembership from "src/Pages/Company/EmployeeManagement/CompanyEmployees/Edit/OrganizationStructure/api/usePotentialAreasToMembership";
import { useLogStatus } from "src/Pages/Company/OrganizationStructure/utils/NewRelicReporter/useLogStatus/useLogStatus";
import { NewRelicLogLevel } from "src/Pages/Company/OrganizationStructure/utils/NewRelicReporter/useLogStatus/useLogStatus.models";

interface IAreasField {
  companyId: string;
  employeeId: string;
  value: any;
  onChange: (value: any) => void;
  isEditable: boolean;
  isDisabled?: boolean;
  label: string;
  error?: { message?: string };
}

const AreasField: FC<IAreasField> = ({
  companyId,
  employeeId,
  value,
  onChange,
  isEditable,
  isDisabled,
  label,
  error,
}) => {
  const logStatus = useLogStatus();
  const [inputValue, setInputValue] = useState<string>("");

  const {
    data,
    hasNextPage,
    fetchNextPage,
    isFetching,
    error: areasError,
  } = useFetchPotentialAreasToMembership({
    companyId,
    employeeId,
    query: inputValue,
  });

  if (areasError) {
    logStatus({
      type: NewRelicLogLevel.ERROR,
      message: "AreasField: fetchPotentialAreasToMembership failed",
      statusDetails: {
        source: "API",
        operation: "fetchPotentialAreasToMembership",
        status: (areasError as AxiosError)?.response?.status,
        url: (areasError as AxiosError)?.config?.url,
      },
    });
  }

  const fetchedAreas = useMemo(
    () =>
      data?.pages.flatMap((page) =>
        page.content.map(
          (area: {
            areaId: string;
            name: string;
            givenEmployeeCanInheritManager: boolean;
            managedByGivenEmployee: boolean;
          }) => ({
            value: area.areaId,
            label: area.name,
            givenEmployeeCanInheritManager: area.givenEmployeeCanInheritManager,
            managedByGivenEmployee: area.managedByGivenEmployee,
          })
        )
      ) || [],
    [data]
  );

  const handleInputChange = useCallback(
    debounce((value) => {
      setInputValue(value);
    }, 1000),
    []
  );

  const handleScrollToBottom = () => {
    if (hasNextPage) {
      fetchNextPage();
    }
  };

  const customStyles = (hasError: boolean) => ({
    control: (provided: any) => ({
      ...provided,
      borderColor: hasError ? "red" : provided.borderColor,
      boxShadow: hasError ? "0 0 0 1px red" : provided.boxShadow,
      "&:hover": {
        borderColor: hasError ? "red" : provided.borderColor,
      },
    }),
  });

  return (
    <>
      <FormGroup>
        {label && <Label for="area">{label}</Label>}
        <Select
          id="area"
          value={value}
          onChange={onChange}
          menuPlacement="auto"
          options={fetchedAreas}
          onInputChange={handleInputChange}
          onMenuScrollToBottom={handleScrollToBottom}
          placeholder={
            isDisabled
              ? __(
                  "Wybrany obszar nie zawiera ustawionego wyświetlanego dodatkowego obszaru"
                )
              : __("Wybierz")
          }
          noOptionsMessage={() => {
            if (isFetching) return __("Ładowanie...");
            return fetchedAreas.length > 0
              ? null
              : __("Brak wyników wyszukiwania");
          }}
          loadingMessage={() => __("Ładowanie...")}
          isClearable
          isLoading={isFetching}
          isDisabled={!isEditable || isDisabled}
          styles={customStyles(!!error)}
        />
      </FormGroup>
    </>
  );
};

export default AreasField;
